import React, {FormEvent, ReactNode, useMemo, useState} from "react"
import {useQuery} from "@apollo/client"
import {useHistory} from "react-router-dom"

import collectionList from "graphql/Media/Collection/query/collectionList"
import movieListSingle from "graphql/Media/Movie/query/movieListSingle"
import homeDiscovery from "graphql/Media/Search/query/homeDiscovery"
import showList from "graphql/Media/Show/query/showList"
import sessionListLatest from "graphql/Streaming/Session/query/sessionListLatest"
import {MediaSlider, MediaTypeEnum} from "components/front/Media/Slider"
import {filterMoviesByGenre, getAvailableMovieGenreGroups, MovieGenreKey} from "helpers/Media/Movie/Genre"
import {URL_FRONT_MOVIES, URL_FRONT_SEARCH} from "static/constant/url"

import "./style.scss"

type HomeSectionProps = {
  index: number
  eyebrow: string
  title: string
  description: string
  countLabel: string
  elementList: any[]
  type: MediaTypeEnum
  tone: string
  toolbar?: ReactNode
}

const HomeSection = ({
  index,
  eyebrow,
  title,
  description,
  countLabel,
  elementList,
  type,
  tone,
  toolbar,
}: HomeSectionProps) => <section className={`home-section home-section--${tone}`}>
  <header className="home-section__header">
    <span className="home-section__index">{String(index).padStart(2, "0")}</span>
    <div className="home-section__heading">
      <span className="home-section__eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
    <span className="home-section__count">{countLabel}</span>
  </header>
  {toolbar}
  <MediaSlider elementList={elementList} type={type} />
</section>

export const MainRoute = () => {
  const history = useHistory()
  const [quickSearch, setQuickSearch] = useState("")
  const [activeMovieGenre, setActiveMovieGenre] = useState<MovieGenreKey>("ALL")
  const showListData = useQuery(showList)
  const movieListData = useQuery(movieListSingle)
  const collectionListData = useQuery(collectionList)
  const sessionListData = useQuery(sessionListLatest, {
    fetchPolicy: "cache-and-network",
  })
  const discoveryData = useQuery(homeDiscovery, {
    variables: {limit: 12},
    fetchPolicy: "cache-and-network",
  })

  const sessions = sessionListData.data?.sessionList || []
  const recent = discoveryData.data?.recent || []
  const friends = discoveryData.data?.friends || []
  const collections = collectionListData.data?.list || []
  const movies = movieListData.data?.movieList || []
  const shows = showListData.data?.showList || []
  const movieGenreGroupList = useMemo(() => getAvailableMovieGenreGroups(movies, 3), [movies])
  const visibleMovies = useMemo(() => filterMoviesByGenre(movies, activeMovieGenre).slice(0, 12), [activeMovieGenre, movies])
  const activeMovieGenreLabel = movieGenreGroupList.find(group => group.key === activeMovieGenre)?.label || "Tous"
  const openMovieLibrary = () => history.push(activeMovieGenre === "ALL"
    ? URL_FRONT_MOVIES
    : `${URL_FRONT_MOVIES}?genre=${activeMovieGenre}`)

  const movieToolbar = <div className="home-movie-toolbar">
    <div className="home-movie-genres" role="group" aria-label="Filtrer les films par genre">
      {movieGenreGroupList.map(group => <button
        key={group.key}
        type="button"
        className={activeMovieGenre === group.key ? "is-active" : ""}
        onClick={() => setActiveMovieGenre(group.key)}
        aria-pressed={activeMovieGenre === group.key}
      >
        {group.label}
      </button>)}
    </div>
    <button type="button" className="home-movies-all" onClick={openMovieLibrary}>
      <span>Voir {activeMovieGenre === "ALL" ? `les ${movies.length} films` : activeMovieGenreLabel}</span>
      <span className="fas fa-arrow-right" aria-hidden="true"></span>
    </button>
  </div>

  const sectionList: Omit<HomeSectionProps, "index">[] = [
    {
      eyebrow: "En cours",
      title: "Là où vous vous êtes arrêté",
      description: "Vos lectures en cours, prêtes à repartir.",
      countLabel: `${sessions.length} en cours`,
      elementList: sessions,
      type: MediaTypeEnum.SESSION,
      tone: "resume",
    },
    {
      eyebrow: "Nouveautés",
      title: "Tout juste arrivé",
      description: "Les derniers titres ajoutés à votre vidéothèque.",
      countLabel: `${recent.length} ajout${recent.length > 1 ? "s" : ""}`,
      elementList: recent,
      type: MediaTypeEnum.MIXED,
      tone: "recent",
    },
    {
      eyebrow: "Entre amis",
      title: "Leurs choix du moment",
      description: "Des titres partagés par votre cercle.",
      countLabel: `${friends.length} suggestion${friends.length > 1 ? "s" : ""}`,
      elementList: friends,
      type: MediaTypeEnum.MIXED,
      tone: "friends",
    },
    {
      eyebrow: "Collections",
      title: "Des univers à parcourir",
      description: "Retrouvez les sagas dans leur ordre naturel.",
      countLabel: `${collections.length} collection${collections.length > 1 ? "s" : ""}`,
      elementList: collections,
      type: MediaTypeEnum.COLLECTION,
      tone: "collections",
    },
    {
      eyebrow: "Films",
      title: "Une histoire pour ce soir",
      description: "Tous les longs métrages de votre catalogue.",
      countLabel: `${movies.length} film${movies.length > 1 ? "s" : ""}`,
      elementList: visibleMovies,
      type: MediaTypeEnum.MOVIE,
      tone: "movies",
      toolbar: movieToolbar,
    },
    {
      eyebrow: "Séries",
      title: "Encore un épisode",
      description: "Vos séries, saison après saison.",
      countLabel: `${shows.length} série${shows.length > 1 ? "s" : ""}`,
      elementList: shows,
      type: MediaTypeEnum.SHOW,
      tone: "shows",
    },
  ].filter(section => section.elementList.length > 0)

  const isInitialLoading = sectionList.length === 0 && [
    showListData,
    movieListData,
    collectionListData,
    sessionListData,
    discoveryData,
  ].some(query => query.loading)

  const openSearch = (event: FormEvent) => {
    event.preventDefault()
    const query = quickSearch.trim()
    history.push(query ? `${URL_FRONT_SEARCH}?q=${encodeURIComponent(query)}` : URL_FRONT_SEARCH)
  }

  return <main className="main-route-container">
    <header className="home-intro">
      <div className="home-intro__copy">
        <span>Votre vidéothèque</span>
        <h1>À regarder maintenant</h1>
      </div>
      <form className="home-quick-search" role="search" onSubmit={openSearch}>
        <label className="sr-only" htmlFor="home-search">Rechercher dans la vidéothèque</label>
        <span className="fas fa-search" aria-hidden="true"></span>
        <input
          id="home-search"
          type="search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Rechercher un titre…"
          value={quickSearch}
          onChange={event => setQuickSearch(event.target.value)}
        />
        <button type="submit" aria-label={quickSearch.trim() ? `Rechercher ${quickSearch.trim()}` : "Ouvrir la recherche"}>
          <span className="fas fa-arrow-right" aria-hidden="true"></span>
        </button>
      </form>
    </header>

    {isInitialLoading && <div className="home-loading" aria-label="Chargement du catalogue">
      <span />
      <span />
      <span />
      <span />
      <span />
    </div>}

    {sectionList.map((section, index) => <HomeSection
      key={section.tone}
      index={index + 1}
      {...section}
    />)}
  </main>
}
